import React from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '@/components/shared/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SearchableSelect from '@/components/shared/SearchableSelect';
import { accountingApi, type LookupBankAccount, type LookupBeneficiary, type BeneficiaryBankAccount } from '@/lib/api/accounting';

const AccountingBankMovementsPage: React.FC = () => {
  const [banks, setBanks] = React.useState<LookupBankAccount[]>([]);
  const [beneficiaries, setBeneficiaries] = React.useState<LookupBeneficiary[]>([]);
  const [beneficiaryBanks, setBeneficiaryBanks] = React.useState<BeneficiaryBankAccount[]>([]);
  const [sourceBank, setSourceBank] = React.useState('');
  const [beneficiary, setBeneficiary] = React.useState('');
  const [beneficiaryBank, setBeneficiaryBank] = React.useState('');
  const [amount, setAmount] = React.useState('');
  const [direction, setDirection] = React.useState<'incoming' | 'outgoing' | 'transfer'>('outgoing');
  const [method, setMethod] = React.useState<'account' | 'card' | 'sheba' | 'cash' | 'cheque'>('account');
  const [refNo, setRefNo] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [rows, setRows] = React.useState<Array<{ id: string; direction: string; amount: number; valueDate?: string | null; referenceNo?: string | null }>>([]);

  React.useEffect(() => {
    Promise.all([accountingApi.lookups(), accountingApi.bankAccounts(), accountingApi.bankMovements()])
      .then(([l, b, m]) => {
        setBeneficiaries(l.beneficiaries || []);
        setBanks(b || []);
        setRows((m || []).map((x) => ({ id: x.id, direction: x.direction, amount: x.amount, valueDate: x.valueDate, referenceNo: x.referenceNo })));
      })
      .catch(() => {});
  }, []);

  React.useEffect(() => {
    if (!beneficiary) return setBeneficiaryBanks([]);
    accountingApi.beneficiaryBankAccounts(beneficiary).then(setBeneficiaryBanks).catch(() => setBeneficiaryBanks([]));
  }, [beneficiary]);

  const save = async () => {
    const [t, i] = beneficiary.split('-');
    await accountingApi.createBankMovement({
      direction,
      amount: Number(amount || 0),
      source_bank_account_id: Number(sourceBank || 0),
      counterparty_type: (i ? (t as 'employee' | 'client' | 'company') : undefined),
      counterparty_id: i ? Number(i) : null,
      counterparty_bank_account_id: beneficiaryBank ? Number(beneficiaryBank) : null,
      payment_method: method,
      reference_no: refNo || undefined,
      description: description || undefined,
    });
    const m = await accountingApi.bankMovements();
    setRows((m || []).map((x) => ({ id: x.id, direction: x.direction, amount: x.amount, valueDate: x.valueDate, referenceNo: x.referenceNo })));
    setAmount('');
    setRefNo('');
    setDescription('');
  };

  return (
    <div className="w-full min-w-0 space-y-6">
      <PageHeader title="ثبت پرداخت/دریافت بانکی" description="ثبت از چه حسابی به چه حسابی" actions={<Button asChild variant="outline"><Link to="/">بازگشت</Link></Button>} />
      <Card>
        <CardHeader><CardTitle className="text-base">فرم ثبت</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <SearchableSelect value={direction} onChange={(v) => setDirection(v as any)} options={[{ value: 'outgoing', label: 'پرداخت' }, { value: 'incoming', label: 'دریافت' }, { value: 'transfer', label: 'انتقال' }]} placeholder="نوع" />
          <SearchableSelect value={method} onChange={(v) => setMethod(v as any)} options={[{ value: 'account', label: 'حساب' }, { value: 'card', label: 'کارت' }, { value: 'sheba', label: 'شبا' }, { value: 'cash', label: 'نقد' }, { value: 'cheque', label: 'چک' }]} placeholder="روش" />
          <SearchableSelect value={sourceBank} onChange={setSourceBank} options={banks.map((x) => ({ value: String(x.id), label: `${x.bank_name} - ${x.name}` }))} placeholder="حساب مبدا" />
          <SearchableSelect value={beneficiary} onChange={setBeneficiary} options={beneficiaries.map((x) => ({ value: x.id, label: `${x.name} (${x.type})` }))} placeholder="طرف حساب" />
          <SearchableSelect value={beneficiaryBank} onChange={setBeneficiaryBank} options={[{ value: '', label: 'اختیاری' }, ...beneficiaryBanks.map((x) => ({ value: String(x.account_id), label: x.label }))]} placeholder="حساب طرف حساب" />
          <Input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="مبلغ" />
          <Input value={refNo} onChange={(e) => setRefNo(e.target.value)} placeholder="شماره پیگیری" />
          <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="شرح" />
          <div className="md:col-span-3 flex justify-end"><Button onClick={save}>ثبت گردش</Button></div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">گردش‌های اخیر</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded border">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-100"><th className="border px-2 py-2 text-right">شناسه</th><th className="border px-2 py-2 text-right">نوع</th><th className="border px-2 py-2 text-right">مبلغ</th><th className="border px-2 py-2 text-right">تاریخ</th><th className="border px-2 py-2 text-right">پیگیری</th></tr></thead>
              <tbody>{rows.map((r) => <tr key={r.id}><td className="border px-2 py-2">{r.id}</td><td className="border px-2 py-2">{r.direction}</td><td className="border px-2 py-2">{Number(r.amount || 0).toLocaleString('en-US')}</td><td className="border px-2 py-2">{r.valueDate || '-'}</td><td className="border px-2 py-2">{r.referenceNo || '-'}</td></tr>)}</tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountingBankMovementsPage;
